import { ApiSuccessResponse } from "../../@types";
import { apiSlice } from "../../app/api/apiSlice";
import {
  AddPlanConfig,
  PlanConfig,
  PlanFeature,
  PlanFeatureRequest,
  PlanFeatureResponse,
  PlanSearchRequest,
  PlanSearchResponse,
  Subscription,
  SubscriptionSearchRequest,
  SubscriptionSearchResponse,
  SubscriptionStats,
  SystemPlan,
  SystemPlanRequest,
  SystemPlanResponse,
} from "./@types";

export const subscriptionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptions: builder.mutation<
      ApiSuccessResponse<SubscriptionSearchResponse>,
      SubscriptionSearchRequest
    >({
      query: (data) => ({
        url: "/v1/subscription/subscription/all",
        method: "POST",
        body: { ...data },
      }),
    }),
    getSubscriptionStats: builder.query<
      ApiSuccessResponse<SubscriptionStats>,
      void
    >({
      query: () => ({
        url: "/v1/systemsettings/custom-pricing",
        method: "GET",
      }),
    }),
    addSubscription: builder.mutation<
      ApiSuccessResponse<any>,
      Record<string, any>
    >({
      query: (data) => ({
        url: "/v1/subscription/subscription",
        method: "POST",
        body: { ...data },
      }),
    }),
    updateSubscription: builder.mutation<
      ApiSuccessResponse<any>,
      Record<string, any>
    >({
      query: (data) => ({
        url: `/v1/subscription/subscription`,
        method: "PUT",
        body: { ...data },
      }),
    }),
    getSubscriptionById: builder.query<
      ApiSuccessResponse<Subscription>,
      string
    >({
      query: (id) => ({
        url: `/subscription/subscriptions/${id}`,
        method: "GET",
      }),
    }),
    getPlanConfig: builder.mutation<
      ApiSuccessResponse<PlanSearchResponse>,
      PlanSearchRequest
    >({
      query: (data) => ({
        url: "/v1/systemsettings/custom-pricing/search",
        method: "POST",
        body: { ...data },
      }),
    }),
    createPlanConfig: builder.mutation<
      ApiSuccessResponse<any>,
      Partial<PlanConfig>
    >({
      query: (data) => ({
        url: "/v1/systemsettings/custom-pricing",
        method: "POST",
        body: { ...data },
      }),
    }),
    updatePlanConfig: builder.mutation<ApiSuccessResponse<any>, AddPlanConfig>({
      query: (data) => ({
        url: `/v1/systemsettings/custom-pricing`,
        method: "PUT",
        body: { ...data },
      }),
    }),
    getPlanConfigForCompany: builder.query<
      ApiSuccessResponse<PlanConfig>,
      string
    >({
      query: (companyId) => ({
        url: `/api/v1/systemsettings/custom-pricing/company/${companyId}`,
        method: "GET",
      }),
    }),
    deletePlanConfig: builder.mutation<ApiSuccessResponse<any>, string>({
      query: (id) => ({
        url: `/v1/systemsettings/custom-pricing/${id}`,
        method: "DELETE",
      }),
    }),
    getPlanFeatures: builder.mutation<
      ApiSuccessResponse<PlanFeatureResponse>,
      PlanFeatureRequest
    >({
      query: (data) => ({
        url: "/v1/planfeatures/all",
        method: "POST",
        body: { ...data },
      }),
    }),
    createPlanFeature: builder.mutation<
      ApiSuccessResponse<any>,
      Partial<PlanFeature>
    >({
      query: (data) => ({
        url: "/v1/planfeatures",
        method: "POST",
        body: { ...data },
      }),
    }),
    updatePlanFeature: builder.mutation<
      ApiSuccessResponse<any>,
      Partial<PlanFeature>
    >({
      query: (data) => ({
        url: `/v1/planfeatures`,
        method: "PUT",
        body: { ...data },
      }),
    }),
    deletePlanFeature: builder.mutation<ApiSuccessResponse<any>, string>({
      query: (id) => ({
        url: `/v1/planfeatures/${id}`,
        method: "DELETE",
      }),
    }),
    getSystemPlan: builder.mutation<
      ApiSuccessResponse<SystemPlanResponse>,
      SystemPlanRequest
    >({
      query: (data) => ({
        url: "/v1/subscriptionsettings/search",
        method: "POST",
        body: { ...data },
      }),
    }),
    createSystemPlan: builder.mutation<
      ApiSuccessResponse<any>,
      Partial<SystemPlan>
    >({
      query: (data) => ({
        url: "/v1/subscriptionsettings",
        method: "POST",
        body: { ...data },
      }),
    }),
    updateSystemPlan: builder.mutation<
      ApiSuccessResponse<any>,
      Partial<SystemPlan>
    >({
      query: (data) => ({
        url: `/v1/subscriptionsettings/${data.id}`,
        method: "PUT",
        body: { ...data },
      }),
    }),
    deleteSystemPlan: builder.mutation<ApiSuccessResponse<any>, string>({
      query: (id) => ({
        url: `/v1/planfeatures/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetSubscriptionsMutation,
  useGetSubscriptionStatsQuery,
  useAddSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useGetSubscriptionByIdQuery,
  // plan config
  useGetPlanConfigMutation,
  useCreatePlanConfigMutation,
  useUpdatePlanConfigMutation,
  useGetPlanConfigForCompanyQuery,
  useDeletePlanConfigMutation,
  //
  useGetPlanFeaturesMutation,
  useCreatePlanFeatureMutation,
  useUpdatePlanFeatureMutation,
  useDeletePlanFeatureMutation,

  // system plan
  useGetSystemPlanMutation,
  useCreateSystemPlanMutation,
  useUpdateSystemPlanMutation,
  useDeleteSystemPlanMutation,
} = subscriptionApiSlice;
